import React, { useState, useEffect } from 'react';
import { TextField, Button, Typography, Box, CircularProgress, Link, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, Link as RouterLink } from 'react-router-dom';

const useStyles = makeStyles((theme) => ({
  form: {
    width: '100%',
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
  backToLogin: {
    marginTop: theme.spacing(2),
    textAlign: 'center',
  },
  error: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(2),
    textAlign: 'center',
  },
  otpContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(2),
  },
  otpInput: {
    width: '3rem',
    textAlign: 'center',
  }
}));

const TwoFactorAuthPage = () => {
  const classes = useStyles();
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(30);
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  // Référence pour les champs OTP
  const otpRefs = Array(6).fill(0).map(() => React.createRef());

  // Timer pour le code OTP
  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [timeLeft]);

  // Gérer le changement dans les champs OTP
  const handleOtpChange = (index, value) => {
    if (value.length > 1) {
      value = value.slice(0, 1);
    }
    
    if (value && /^[0-9]$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      
      // Passer au champ suivant
      if (index < 5 && value) {
        otpRefs[index + 1].current.focus();
      }
    } else if (value === '') {
      const newOtp = [...otp];
      newOtp[index] = '';
      setOtp(newOtp);
    }
  };

  // Gérer la touche Backspace
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs[index - 1].current.focus();
    }
  };

  // Gérer la soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const otpValue = otp.join('');
    if (otpValue.length !== 6) {
      setError('Veuillez entrer le code à 6 chiffres complet');
      return;
    }
    
    try {
      setError('');
      setLoading(true);
      
      // Simulation de vérification OTP
      setTimeout(() => {
        // Pour la démo, on accepte n'importe quel code complet
        navigate('/admin');
      }, 1500);
      
    } catch (error) {
      setError('Code incorrect. Veuillez réessayer.');
      console.error('2FA error:', error);
      setLoading(false);
    }
  };

  // Renvoyer un nouveau code
  const resendCode = () => {
    setTimeLeft(30);
    // Simulation d'envoi de nouveau code
    setTimeout(() => {
      console.log('Nouveau code envoyé');
    }, 1000);
  };

  return (
    <Box width="100%">
      <Typography variant="h6" align="center" gutterBottom>
        Authentification à deux facteurs
      </Typography>
      
      <Typography variant="body2" align="center" gutterBottom>
        Un code de vérification a été envoyé à votre appareil. Veuillez l'entrer ci-dessous.
      </Typography>
      
      <form className={classes.form} onSubmit={handleSubmit} noValidate>
        <Box className={classes.otpContainer}>
          {otp.map((digit, index) => (
            <TextField
              key={index}
              variant="outlined"
              inputRef={otpRefs[index]}
              className={classes.otpInput}
              value={digit}
              onChange={(e) => handleOtpChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              inputProps={{ maxLength: 1, style: { textAlign: 'center' } }}
            />
          ))}
        </Box>
        
        {error && (
          <Typography variant="body2" className={classes.error}>
            {error}
          </Typography>
        )}
        
        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          className={classes.submit}
          disabled={loading || otp.join('').length !== 6}
        >
          {loading ? <CircularProgress size={24} /> : 'Vérifier'}
        </Button>
        
        <Grid container justifyContent="center" spacing={2}>
          <Grid item>
            <Button 
              color="primary" 
              disabled={timeLeft > 0} 
              onClick={resendCode}
            >
              {timeLeft > 0 ? `Renvoyer le code (${timeLeft}s)` : 'Renvoyer le code'}
            </Button>
          </Grid>
        </Grid>
        
        <Box className={classes.backToLogin}>
          <Link component={RouterLink} to="/login" variant="body2">
            Retour à la connexion
          </Link>
        </Box>
      </form>
    </Box>
  );
};

export default TwoFactorAuthPage;
